import * as Sentry from '@sentry/react';
import type { Account } from '@meitu/account-web';
import type { OpenPageOptions } from '@meitu/account-services';
import {
  getGid, trackGoogleEvent, trackMetaEvent, trackTikTokEvent,
} from '@/utils';
import { getEnv } from '@/common/agents';
import LoginLeftContent from '@/components/LoginLeftContent';

import localStorageUtil from '$/common/utils/localStorageUtil';
import { useLanguageStore } from '$/store/languageStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import type { User } from '$/store/useOfficialStore/types';
import { mtstatTrack } from '$/utils/mtstat';

import Cookies from 'js-cookie';

/**
 * 调用基于帐号网页版的扩展组件
 * @link https://f2er.meitu.com/account/docs/guides/usages/initialization/
 */

function getEnvValue(env?: string) {
  // 1 test, 0 线上（默认）, 2 beta
  let value;
  switch (env) {
    case 'release':
      value = 0;
      break;
    case 'beta':
      value = 2;
      break;
    default:
      value = 1;
  }
  return value;
}
let AccountSDK: any;

type AccountLoginResult = Account & {
  status_register_app?: number | string;
  statusRegisterApp?: number | string;
  user?: {
    status_register_app?: number | string;
    statusRegisterApp?: number | string;
  };
};

/**
 * 判断账号 SDK 返回的本次登录是否为 App 新注册，兼容 snake_case 与 camelCase 字段。
 */
function isAppRegisterAccount(result: AccountLoginResult) {
  const registerStatus = result.status_register_app
    ?? result.statusRegisterApp
    ?? result.user?.status_register_app
    ?? result.user?.statusRegisterApp;

  return Number(registerStatus) === 1;
}

export const initMTLogin = async ({ isOversea }: { isOversea: boolean; }) => {
  if (AccountSDK) return AccountSDK;
  const { MeituAccount } = await import('@meitu/account-web');
  const { currentCountryCode } = useOfficialStore.getState();
  const { languageCode } = useLanguageStore.getState().languageStore;
  AccountSDK = MeituAccount({
    env: getEnvValue(getEnv().ENV), // 指定环境 0 正式环境（默认）、1 Preview 测试环境、2、Beta 拟真测试环境
    clientId: getEnv().CLIENT_ID,
    theme: 'v3',
    overseas: isOversea ? 1 : undefined,
    // gid: '', // TODO 补充gid
    clientLanguage: languageCode,
    // debug: true, // 可选；是否开启调试模式
    // sourceFrom: 'sdk-dev-demo', // 可选；自定义来源统计标识
    clientConfigs: {
      // needPhone: true,
      abroad: !!isOversea,
      countryCode: currentCountryCode,
      enableWeChatOfficialAccount: false, // 开启关注公众号登录
      abroadEnablePhone: false, // 是否开启海外手机登录
      // 是否需要勾选协议
      enableAgreementCheck: !isOversea,
      googleClientId: isOversea ? '536560286350-rvsg9umdmpf42lmdhc5fg18eauf26hn4.apps.googleusercontent.com' : undefined,
    },
    syncDomains: false, // 可选；是否开启跨域名（非 *.meitu.com 域）同步登录和退出状态（HTTPS 域名才有效，目前只支持 Chrome 系浏览器，不支持 Safari 和火狐浏览器
  });
  return AccountSDK;
};

export const logoutPopup = () => {
  AccountSDK.logout({
    silentSyncLogout: true,
  });
};

/**
 * 打开登录弹窗，并在新用户注册成功时上报渠道归因事件
 */
export const loginPopup = async ({ isOversea, isMobile }: { isOversea: boolean; isMobile: boolean; }): Promise<Account> => {
  removeAccessToken();
  removeUID();
  const { openLoginPopup } = await import('@meitu/account-login-popup');
  const { ThirdPartyPlatform } = await import('@meitu/account-web');

  let thirdPartyList: typeof ThirdPartyPlatform[keyof typeof ThirdPartyPlatform][] = [];
  if (isOversea) {
    thirdPartyList = isMobile ? [
      ThirdPartyPlatform.Email,
      ThirdPartyPlatform.Google,
    ] : [
      ThirdPartyPlatform.Email,
      ThirdPartyPlatform.Google,
      ThirdPartyPlatform.Facebook,
      ThirdPartyPlatform.Apple,
    ];
  } else {
    thirdPartyList = isMobile ? [] : [
      ThirdPartyPlatform.WeChat,
      ThirdPartyPlatform.Meitu,
      ThirdPartyPlatform.Apple,
    ];
  }
  // const thirdPartyList = isOversea ? [
  //   ThirdPartyPlatform.Email,
  //   ThirdPartyPlatform.Google,
  //   ThirdPartyPlatform.Facebook,
  //   ThirdPartyPlatform.Apple,
  // ] : [
  //   ThirdPartyPlatform.WeChat,
  //   ThirdPartyPlatform.Meitu,
  //   ThirdPartyPlatform.Apple,
  // ];

  return new Promise((resolve, reject) => {
    const { languageStore } = useLanguageStore.getState();
    openLoginPopup({
      // ...登录弹窗配置选项，详见：LoginPopupProps
      // 第三方登录列表
      thirdPartyAccounts: thirdPartyList,
      header: languageStore?.smartcatTranslationText((v) => v.roboneo_web_welcome),
      title: '',
      showMeituXiuXiuQRCode: false,
      leftSide: () => {
        return LoginLeftContent();
      },
      // 隐藏关闭按钮
      showCloseButton: true,
      autoDetectAndSetDarkTheme: false,
      closeable: true,
      switch: true, // 切换账号，会先清除登录状态
      onClose() {
        // ATTENTION: 登录成功和主动关闭弹窗都会执行
        // 关闭弹窗后的自定义处理，略...
      },
      onLoginSuccess: async (result) => {
        console.log('onLoginSuccess ====>', result);
        setAccessToken(result.accessToken);
        setUID(`${result.uid}`);
        const isAppRegister = isAppRegisterAccount(result);
        if (isAppRegister) {
          // 注册成功后使用账号 SDK 返回的 uid 和当前页面地址上报大禹埋点。
          mtstatTrack('roboneo_account_registersucess', {
            uid: `${result.uid}`,
            page_url: window.location.href,
          });
          // 新注册用户上报事件到 GA 配合渠道投放归因
          trackGoogleEvent('roboneo_account_registersucess', {
            // eslint-disable-next-line
            event_callback() {
              resolve(result);
            },
          });
          // 新注册用户上报 Meta 标准注册事件，配合渠道投放归因
          trackMetaEvent('track', 'CompleteRegistration', {});
          // 新注册用户上报 TikTok 前端标准注册事件，配合渠道投放归因
          trackTikTokEvent('CompleteRegistration', {});
          return;
        }
        resolve(result);
      },
      onLogoutSuccess(silent) {
        console.log('onLogoutSuccess ====>', { silent });
      },
      onLoginError: reject,
      onLogoutError: reject,
      useLoginMethod: isOversea ? 'third-party' : '',

    });
  });
};

const TOKEN_CACHE_KEY = 'access_token';

export function removeAccessToken() {
  localStorageUtil.removeItem(TOKEN_CACHE_KEY);
  document.cookie = `${TOKEN_CACHE_KEY}=; path=/; max-age=0; SameSite=Lax`;
}

export function setAccessToken(accessToken: string) {
  localStorageUtil.setItem(TOKEN_CACHE_KEY, accessToken);
  document.cookie = `${TOKEN_CACHE_KEY}=${encodeURIComponent(accessToken)}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`;
}

export function getAccessToken() {
  const token = localStorageUtil.getItem(TOKEN_CACHE_KEY) || '';
  if (token && typeof document !== 'undefined') {
    const hasCookie = document.cookie.split('; ').some((c) => c.startsWith(`${TOKEN_CACHE_KEY}=`));
    if (!hasCookie) {
      document.cookie = `${TOKEN_CACHE_KEY}=${encodeURIComponent(token)}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`;
    }
  }
  return token;
}
export async function getUserInfo(): Promise<{
  data: {
    meta: {
      code: number;
    };
    response: {
      user: {
        avatar: string;
        screenName: string;
        id: number;
        phoneCc?: number | string;
        phone?: string;
      };
    };
  };
} | null> {
  const isLogin = await AccountSDK.isLogin();
  !isLogin && Sentry.captureMessage('getUserInfo isLogin', {
    level: 'warning',
    extra: {
      gid: getGid(),
    },
  });
  if (isLogin) {
    // 已登录过的情况下，账号 JS SDK 本身有存储用户信息，可以通过 account.user 对象获取
    // const userInfo = account.user

    // 但是很久未重新登录时，有可能是旧的用户信息，需要重新请求获取可获取最新的用户信息
    const res = await AccountSDK.getUserInfo();
    return res;
  }
  return null;
}

const UID_CACHE_KEY = 'uid';

export function removeUID() {
  localStorageUtil.removeItem(UID_CACHE_KEY);
  Cookies.remove(UID_CACHE_KEY, { path: '/' });
}

export function setUID(uid: string) {
  localStorageUtil.setItem(UID_CACHE_KEY, uid);
  Cookies.set(UID_CACHE_KEY, uid, { path: '/' });
}

export function getUID() {
  const uid = localStorageUtil.getItem(UID_CACHE_KEY) || '';
  // 将localStorage的uid同步到Cookie
  Cookies.set(UID_CACHE_KEY, uid, { path: '/' });
  return uid;
}
export function createDefaultUser(): User {
  return {
    uid: '',
    avatar: '',
    screenName: '',
    token: '',
    isChinaPhone: false,
  };
}

// 打开账号信息页面
export function openAccountProfilePage(
  isOversea: boolean,
  options?: OpenPageOptions,
) {
  // https://f2er.meitu.com/account/api/account-model/interface/AccountQueryParams#clientId
  // 获取主题
  const theme = Cookies.get('theme');
  // console.log(theme)
  // options.
  AccountSDK.openPage(undefined, {
    clientId: getEnv().CLIENT_ID,
    clientCallback: '0',
    // redirectCallback: '业务重定向地址',
    overseas: isOversea ? 1 : undefined,
    theme,
    // clientConfig 配置： https://f2er.meitu.com/account/api/account-model/interface/ClientConfigs
    clientConfigs: {
      abroad: !!isOversea,
      showAccountCancellation: true,
      showSecurityCancellation: false,
      enableIdentityAuth: false,
      enableAddressManagement: false,
    },
  }, options);
}
